/**
 * 6love · Data-visualization palette
 *
 * The single source of truth for analytics colors — win vs loss, the
 * easier→harder difficulty ramp, neutral chart chrome, and a categorical
 * series palette. Previously these were hard-coded as raw hex inside each
 * chart; promote them here so every chart, badge, and metric pulls from one
 * place.
 *
 * This is the app-side (recharts / inline-SVG) mirror of the design system's
 * `tokens/data-viz.css`. The `--data-*` token name each value maps to is noted
 * inline; keep the two in sync.
 */

/** Outcome — win / loss / tie. Aligned to the semantic status palette. */
export const OUTCOME = {
  win: '#16a34a', // --data-win  (status-available)
  winSoft: '#dcfce7', // --data-win-soft
  winText: '#166534', // --data-win-text
  loss: '#dc2626', // --data-loss (status-unavailable)
  lossSoft: '#fee2e2', // --data-loss-soft
  lossText: '#991b1b', // --data-loss-text
  tie: '#9ca3af', // --data-tie
  tieSoft: '#f3f4f6', // --data-tie-soft
} as const;

/** Chart chrome — gridlines, axes, reference lines, hover band. */
export const CHROME = {
  grid: '#e5e7eb', // --data-grid     (gray-200)
  axis: '#6b7280', // --data-axis     (gray-500)
  reference: '#9ca3af', // --data-reference (gray-400)
  cursor: 'rgba(17, 24, 39, 0.04)', // --data-cursor
} as const;

/** Strength-of-schedule difficulty ramp, easier → harder (diverging). */
export const DIFFICULTY_RAMP = {
  'much-easier': '#15803d', // --data-diff-much-easier
  easier: '#22c55e', // --data-diff-easier
  even: '#9ca3af', // --data-diff-even
  harder: '#f97316', // --data-diff-harder
  'much-harder': '#c2410c', // --data-diff-much-harder
} as const;

/**
 * Categorical series palette for multi-line / multi-bar charts. Brand green
 * leads; use in order and wrap after 6 (`--series-1…6`).
 */
export const SERIES = [
  '#1e8554', // brand green
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
] as const;
